/**
 * Quotation Completeness Indicator.
 *
 * Author: Akarsh Singh | Created on: 2026-08-17
 * Updated on: 2026-08-17 — missing-fields panel moved into a CDK overlay so it
 *                          can no longer be hidden behind the form; added a
 *                          second, separate signal for RECOMMENDED (optional)
 *                          fields.
 * Purpose: a compact strip that sits under the "Create Quotation" hero title on
 *          every quotation step and tells the supplier how much of the current
 *          step is filled in, which mandatory fields are still empty, and which
 *          optional fields would make the quote more useful to the buyer.
 *
 * TWO SIGNALS, ONE MEANING EACH — this separation is the whole design:
 *
 *   The PERCENTAGE and the bar measure REQUIRED fields only. That keeps "100%"
 *   meaning exactly one thing: "I can submit this now". If optional fields were
 *   folded into the number, 100% would be practically unreachable and a supplier
 *   sitting at 78% could not tell whether they were blocked or merely untidy.
 *
 *   RECOMMENDED fields get their own count, their own colour and their own
 *   panel. They never move the bar and never block anything.
 *
 * Colour carries that meaning and must not be reused loosely:
 *   red / orange = required, blocks submission
 *   teal         = recommended, only improves the quote
 *
 * This component is deliberately "dumb": it renders whatever lists the host page
 * hands it and reports back which entry the supplier clicked. All knowledge of
 * WHAT is mandatory or recommended stays in the page that owns the form, so the
 * percentage can never drift away from the validation that already blocks
 * submission on that page.
 */
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

/** Which of the two lists a panel is showing. */
export type QuoteFieldGroup = 'required' | 'recommended';

/** One field the supplier has not filled in yet. */
export interface QuoteMissingField {
  /** Label shown to the supplier, e.g. "Price Good Until". */
  label: string;
  /** Where the field lives, e.g. "Quotation Details". Shown greyed on the right. */
  section: string;
  /**
   * Reactive-form control name, used by the host page to scroll to and focus the
   * field. Optional because some entries (a line-item cell) are not a single
   * named control.
   */
  controlName?: string;
  /** Line-item row this entry belongs to, when the field lives inside the table. */
  rowIndex?: number;
  /** Step to jump to when the strip is shown on the Preview page. */
  tabRoute?: string;
  /**
   * One short line saying what the BUYER gains if this is filled in. Only used
   * for recommended fields - "Recommended" on its own persuades nobody, so the
   * benefit is the part that actually earns the supplier's time.
   */
  benefit?: string;
  /**
   * CSS selector to scroll to when the field has no usable `formControlName`.
   *
   * Not every value on these forms is a plain reactive control: the incoterm
   * location picker binds a standalone `[formControl]`, and attachments are
   * managed by an upload widget with no input of their own. Those render no
   * `formcontrolname` attribute, so a control-name lookup silently finds
   * nothing and the click appears to do nothing at all.
   */
  anchorSelector?: string;
  /**
   * Collapsible section that must be opened before this field can be reached.
   *
   * Sections on the header screen are `*ngIf`-ed, so a field inside a collapsed
   * one is not merely off-screen - it is absent from the DOM entirely, and no
   * amount of scrolling will find it.
   */
  sectionKey?: string;
}

/**
 * Bring a field into view and put the cursor in it.
 *
 * Two ways of finding the element, tried in order:
 *
 *   1. `formcontrolname` - Angular renders this attribute for any control bound
 *      with `formControlName`, so most fields need nothing added to the markup.
 *   2. `anchorSelector` - for the fields that render no such attribute. The
 *      incoterm location picker binds a standalone `[formControl]`, and
 *      attachments are handled by an upload widget with no input of their own;
 *      both used to fail lookup 1 silently, which is why clicking those rows
 *      appeared to do nothing.
 *
 * Focus is only attempted on elements that can actually take it, so pointing an
 * anchor at a wrapper `div` scrolls without throwing.
 */
export function scrollToQuoteField(field?: QuoteMissingField): void {
  if (!field) {
    return;
  }

  let element: HTMLElement | null = null;

  if (field.controlName) {
    element = document.querySelector(
      `[formcontrolname="${field.controlName}"]`
    ) as HTMLElement | null;
  }

  if (!element && field.anchorSelector) {
    element = document.querySelector(field.anchorSelector) as HTMLElement | null;
  }

  // Still nothing: the section may be collapsed, or the field may not be
  // rendered on this screen. Do nothing rather than throw.
  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Focus after the smooth scroll has settled, otherwise the browser jumps to
  // the field instantly and the scroll animation is wasted.
  setTimeout(() => {
    const isFocusable =
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement ||
      element!.hasAttribute('tabindex');

    if (isFocusable && typeof element!.focus === 'function') {
      element!.focus();
    }
  }, 350);
}

@Component({
  selector: 'app-quote-completeness',
  templateUrl: './quote-completeness.component.html',
  styleUrls: ['./quote-completeness.component.scss'],
})
export class QuoteCompletenessComponent implements OnDestroy {
  /** How many MANDATORY checks this step has in total. 0 hides the whole strip. */
  @Input() total = 0;

  /** The mandatory checks that are currently unsatisfied. Drives the bar. */
  @Input() missing: QuoteMissingField[] = [];

  /**
   * Optional fields worth filling in. These never affect the bar, the
   * percentage, or the ability to submit.
   */
  @Input() recommended: QuoteMissingField[] = [];

  /**
   * Heading inside the panel, e.g. "Header details". The remaining count is
   * appended by the template.
   */
  @Input() scopeLabel = '';

  /** Raised when the supplier picks a row from either panel. */
  @Output() fieldSelected = new EventEmitter<QuoteMissingField>();

  /** Which panel is open, if any. Both are collapsed on load, by design. */
  openGroup: QuoteFieldGroup | null = null;

  @ViewChild('requiredButton') requiredButton?: ElementRef<HTMLElement>;
  @ViewChild('recommendedButton') recommendedButton?: ElementRef<HTMLElement>;
  @ViewChild('panelTemplate') panelTemplate?: TemplateRef<unknown>;

  private overlayRef?: OverlayRef;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  /**
   * Completion for this step, 0-100, counting MANDATORY fields only.
   * An empty checklist counts as complete.
   */
  get percent(): number {
    if (!this.total || this.total <= 0) {
      return 100;
    }
    const satisfied = this.total - this.requiredCount;
    const value = Math.round((satisfied / this.total) * 100);
    // Guard against a host passing more missing entries than the declared total.
    return Math.min(100, Math.max(0, value));
  }

  get requiredCount(): number {
    return this.missing?.length || 0;
  }

  get recommendedCount(): number {
    return this.recommended?.length || 0;
  }

  /** Nothing left to do at all - neither required nor recommended. */
  get isEverythingFilled(): boolean {
    return this.requiredCount === 0 && this.recommendedCount === 0;
  }

  /** Submittable, but there are still optional fields worth filling. */
  get isReadyWithSuggestions(): boolean {
    return this.requiredCount === 0 && this.recommendedCount > 0;
  }

  /* ---- what the open panel shows ---- */

  get panelItems(): QuoteMissingField[] {
    return this.openGroup === 'recommended' ? this.recommended : this.missing;
  }

  get panelIsRecommended(): boolean {
    return this.openGroup === 'recommended';
  }

  get panelTitle(): string {
    return this.panelIsRecommended
      ? 'Recommended'
      : 'Must fill before submitting';
  }

  togglePanel(group: QuoteFieldGroup): void {
    if (this.openGroup === group) {
      this.closePanel();
      return;
    }
    this.openPanel(group);
  }

  /**
   * Open a panel in a CDK overlay, anchored to the pill that was clicked.
   *
   * WHY AN OVERLAY, and not a positioned child of this component:
   * the hero banner that hosts this strip runs an entrance animation whose
   * keyframes animate `transform`, and the animation is declared with
   * `fill-mode: both`. A filled transform makes that banner the containing block
   * for any fixed-position descendant AND gives it its own stacking context, so
   * a panel rendered inside it stayed trapped in the banner - clipped by the
   * banner's `overflow: hidden` and painted underneath the form card below it.
   * The CDK overlay renders into a container appended to <body>, which is
   * outside the banner entirely, so neither the clipping nor the stacking order
   * can reach it. No page had to relax its own CSS for this.
   *
   * The overlay is rebuilt on each open because the two pills are different
   * anchors, and a position strategy is bound to the element it was built for.
   */
  private openPanel(group: QuoteFieldGroup): void {
    const anchor =
      group === 'recommended' ? this.recommendedButton : this.requiredButton;

    if (!anchor || !this.panelTemplate) {
      return;
    }

    this.disposeOverlay();
    this.openGroup = group;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(anchor)
      // Preferred below the pill; flips above it when the viewport is short.
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -8,
        },
      ])
      // Nudge back into view rather than hanging off the edge on small screens.
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      // A transparent backdrop gives us click-outside-to-close for free, and
      // keeps the click from also reaching the page underneath.
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.overlayRef.backdropClick().subscribe(() => this.closePanel());
    this.overlayRef.attach(
      new TemplatePortal(this.panelTemplate, this.viewContainerRef)
    );
  }

  closePanel(): void {
    this.disposeOverlay();
    this.openGroup = null;
  }

  private disposeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }

  onFieldClick(field: QuoteMissingField): void {
    this.closePanel();
    this.fieldSelected.emit(field);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closePanel();
  }

  ngOnDestroy(): void {
    this.disposeOverlay();
  }

  /** Keeps *ngFor from re-creating rows on every change-detection pass. */
  trackByField(_index: number, field: QuoteMissingField): string {
    return `${field.section}|${field.label}`;
  }
}
